// ============================================================================
// REQUIREMENTS MIRROR WIDGET - Full-screen persona + user stories mirror
// Follows the main screen's active persona tab and scroll position
// ============================================================================

import React, { useRef, useEffect } from 'react';
import { CheckCircle, Settings } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';
import type { ExtractedPersona, UserStory } from '../../../services/workshop';

const PRIORITY_CONFIG = {
  high: { stripe: 'bg-red-500', badge: 'bg-red-100 text-red-700', label: 'HIGH' },
  medium: { stripe: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'MED' },
  low: { stripe: 'bg-slate-300', badge: 'bg-slate-100 text-slate-600', label: 'LOW' },
};

function storiesForPersona(personaName: string, allStories: UserStory[]): UserStory[] {
  const pName = personaName.toLowerCase().trim();
  return allStories.filter(story => {
    const raw = (story as any).persona || (story as any).as_a || '';
    const sPersona = String(raw).toLowerCase().trim();
    if (!sPersona) return false;
    return sPersona === pName || sPersona.includes(pName) || pName.includes(sPersona);
  }).sort((a, b) => {
    const po: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return (po[a.priority] || 2) - (po[b.priority] || 2);
  });
}

function storyText(story: any): string {
  if (story.story) return story.story;
  const soThatSuffix = story.so_that ? ' so that ' + story.so_that : '';
  return story.as_a
    ? `As ${story.as_a}, I want ${story.i_want || ''}${soThatSuffix}`
    : story.title || '';
}

interface SessionContextPanelProps {
  assumptions: unknown[];
}

const SessionContextPanel: React.FC<SessionContextPanelProps> = ({ assumptions }) => (
  <div className="flex flex-col gap-6 max-w-4xl mx-auto">
    {assumptions.map((item) => (
      <div key={typeof item === 'string' ? item : JSON.stringify(item)} className="flex items-start gap-3 text-base text-slate-600 bg-white rounded-xl px-5 py-3.5 border border-slate-200 shadow-sm">
        <span className="w-2 h-2 rounded-full bg-amber-400 mt-2 shrink-0" />
        <span>{typeof item === 'string' ? item : (item as any).description || (item as any).assumption || JSON.stringify(item)}</span>
      </div>
    ))}
  </div>
);

interface PersonaPanelProps {
  persona: ExtractedPersona;
  initials: string;
  stories: UserStory[];
}

const PersonaPanel: React.FC<PersonaPanelProps> = ({ persona, initials, stories }) => (
  <div className="flex flex-col gap-6 max-w-4xl mx-auto">

    {/* Persona header card */}
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center gap-5 mb-5">
        <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xl shrink-0">
          {initials}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-navy leading-tight">{persona.name}</h2>
          <p className="text-base text-slate-500 mt-0.5">{persona.role}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {persona.description && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">About</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              {typeof persona.description === 'string'
                ? persona.description
                : Object.values(persona.description as Record<string, string>).join(' ')}
            </p>
          </div>
        )}
        {persona.goals.length > 0 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Goals</p>
            <ul className="space-y-1.5">
              {persona.goals.map((goal) => (
                <li key={goal} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                  {goal}
                </li>
              ))}
            </ul>
          </div>
        )}
        {persona.painPoints.length > 0 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Pain Points</p>
            <ul className="space-y-1.5">
              {persona.painPoints.map((pain) => (
                <li key={pain} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                  {pain}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>

    {/* User stories */}
    {stories.length > 0 && (
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
          User Stories ({stories.length})
        </p>
        <div className="flex flex-col gap-3">
          {stories.map(story => {
            const pc = PRIORITY_CONFIG[story.priority] ?? PRIORITY_CONFIG.low;
            return (
              <div
                key={story.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative"
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${pc.stripe}`} />
                <div className="p-5 pl-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold tracking-wider ${pc.badge}`}>
                      {pc.label}
                    </span>
                  </div>
                  <p className="text-base text-slate-800 leading-relaxed">{storyText(story)}</p>
                  {story.acceptanceCriteria.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                        Acceptance Criteria
                      </p>
                      <ul className="space-y-1.5">
                        {story.acceptanceCriteria.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle size={14} className="mt-0.5 shrink-0 text-primary" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

const RequirementsMirrorWidget: React.FC<WidgetProps> = () => {
  const { state } = useSecondScreenData();
  const storiesScrollRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef<number>(0);

  const personas = state?.requirements?.personas ?? [];
  const allStories = state?.requirements?.userStories ?? [];
  const focus = state?.requirementsFocus ?? null;

  // Determine active persona
  const activePersona = focus?.activePersonaId
    ? personas.find(p => p.id === focus.activePersonaId) ?? personas[0] ?? null
    : personas[0] ?? null;

  // Sync scroll position from main screen
  useEffect(() => {
    if (!storiesScrollRef.current || focus == null) return;
    const newScrollTop = focus.storiesScrollTop;
    if (newScrollTop !== lastScrollTopRef.current) {
      lastScrollTopRef.current = newScrollTop;
      storiesScrollRef.current.scrollTop = newScrollTop;
    }
  }, [focus?.storiesScrollTop]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || personas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 bg-slate-50">
        <p className="text-slate-400 text-sm">No personas extracted yet</p>
      </div>
    );
  }

  const activePersonaStories = activePersona ? storiesForPersona(activePersona.name, allStories) : [];
  const activeInitials = activePersona
    ? activePersona.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : '';

  const isSessionContextActive = focus?.activePersonaId === 'session-context';

  let contentSection: React.ReactNode;
  if (isSessionContextActive) {
    contentSection = <SessionContextPanel assumptions={state.requirements?.assumptions ?? []} />;
  } else if (activePersona) {
    contentSection = (
      <PersonaPanel persona={activePersona} initials={activeInitials} stories={activePersonaStories} />
    );
  } else {
    contentSection = null;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">

      {/* Persona tab strip */}
      <div className="flex overflow-x-auto border-b border-slate-200 bg-white shrink-0 scrollbar-hide">
        {personas.map(persona => {
          const initials = persona.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
          const pCount = storiesForPersona(persona.name, allStories).length;
          const isActive = !isSessionContextActive && persona.id === (activePersona?.id ?? null);
          return (
            <div
              key={persona.id}
              className={`flex items-center gap-3 px-5 py-3.5 shrink-0 border-b-2 transition-all ${
                isActive
                  ? 'border-primary bg-white text-navy'
                  : 'border-transparent text-slate-500'
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                isActive ? 'bg-purple-100 text-purple-700' : 'bg-slate-200 text-slate-500'
              }`}>
                {initials}
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold truncate max-w-[200px]">{persona.name}</div>
                <div className="text-sm truncate max-w-[200px] text-slate-500">{persona.role}</div>
              </div>
              {pCount > 0 && (
                <span className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${
                  isActive ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-500'
                }`}>
                  {pCount}
                </span>
              )}
            </div>
          );
        })}

        {/* Session Context tab */}
        {((state.requirements?.technicalSpecs?.coreFunctionality?.length ?? 0) > 0 ||
          (state.requirements?.assumptions?.length ?? 0) > 0) && (
          <>
            <div className="w-px bg-slate-200 my-2 mx-1 shrink-0" />
            <div className={`flex items-center gap-2.5 px-5 py-3.5 shrink-0 border-b-2 transition-all ${
              isSessionContextActive ? 'border-primary bg-white text-navy' : 'border-transparent text-slate-500'
            }`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                isSessionContextActive ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'
              }`}>
                <Settings size={16} />
              </div>
              <div>
                <div className="text-base font-bold">Session Context</div>
                <div className="text-sm text-slate-500">Specs &amp; assumptions</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div ref={storiesScrollRef} className="flex-1 overflow-y-auto p-6">
        {contentSection}
      </div>
    </div>
  );
};

export default RequirementsMirrorWidget;
